import { API } from 'aws-amplify'
import { LimitOffsetCursor, ListResponse } from './types'
import { getNextCursor } from './utils'

export enum NotificationType {
  Slack = 'Slack',
  MicrosoftTeams = 'Microsoft Teams',
  PagerDuty = 'Pager Duty',
  Webhook = 'Webhook',
  Email = 'Email',
}

export type SlackChannel = { id: string; name: string }
export type SlackMember = { id: string; name: string }
export type TeamsChannel = { id: string; name: string }

export type NotificationItem = {
  notification_id: string
  notification_type: NotificationType
  destination: NotificationDestination
  activity: NotificationActivity[]
}

export type NotificationDestination = {
  channel?: SlackChannel | TeamsChannel
  integration_key?: string
  severity?: 'critical' | 'warning' | 'error' | 'info'
  webhook_url?: string
  mentions?: SlackMember[]
  emails?: string[]
}

export type NotificationActivity = {
  channel: 'events' | 'people' | 'things'
  eventName: 'create' | 'update'
  filters: string[]
  category_names?: string
  event_type_names?: string
  thing_type_names?: string
}

type NotificationActivityInputItem = {
  enabled: boolean
  filters: string[]
}

export type NotificationActivityInput = {
  events_create?: NotificationActivityInputItem
  people_create?: NotificationActivityInputItem
  things_create?: NotificationActivityInputItem
  events_update?: NotificationActivityInputItem
  people_update?: NotificationActivityInputItem
  things_update?: NotificationActivityInputItem
}

export type NotificationFormValues = Omit<NotificationDestination, 'emails'> & {
  notification_id?: string
  notification_type: NotificationType
  emails: string[]
  activity: NotificationActivityInput
}

function convertNotificationActivity(values: NotificationActivityInput) {
  const activity: NotificationActivity[] = []
  for (const key in values) {
    const [channel, eventName] = key.split('_') as [NotificationActivity['channel'], NotificationActivity['eventName']]
    const value = values[key as keyof typeof values]
    if (value?.enabled) {
      activity.push({ channel, eventName, filters: value.filters })
    }
  }
  return activity
}

function convertNotificationFormValues({
  notification_id,
  notification_type,
  activity,
  channel,
  emails,
  mentions,
  webhook_url,
  integration_key,
  severity,
}: NotificationFormValues) {
  let destination: NotificationDestination = {}
  if (notification_type === NotificationType.Slack) {
    destination = { channel, mentions }
  } else if (notification_type === NotificationType.MicrosoftTeams) {
    destination = { channel, webhook_url }
  } else if (notification_type === NotificationType.PagerDuty) {
    destination = { integration_key, severity }
  } else if (notification_type === NotificationType.Webhook) {
    destination = { webhook_url }
  } else if (notification_type === NotificationType.Email) {
    destination = { emails }
  }
  return {
    notification_id,
    notification_type,
    destination,
    webhook_url,
    activity: convertNotificationActivity(activity),
  }
}

export function getNotifications(): Promise<NotificationItem[]> {
  return API.get('notifications', '/notifications', {})
}

export const getNotificationsList = async (cursor: LimitOffsetCursor = {}): Promise<ListResponse<NotificationItem>> => {
  const response = await API.get('notifications', '/notifications', {
    queryStringParameters: {
      responseFormat: 'list',
      ...cursor,
    },
  })

  return {
    ...response,
    nextCursor: getNextCursor(response, cursor),
  }
}

type FetchNotificationParams = { id: string }

export async function fetchNotification({ id }: FetchNotificationParams): Promise<NotificationItem> {
  return API.get('notifications', `/notifications/${id}`, {})
}

export async function createNotification(body: NotificationFormValues) {
  return API.post('notifications', '/notifications', {
    body: convertNotificationFormValues(body),
  })
}

export function updateNotification(notification: NotificationItem, body: NotificationFormValues) {
  return API.put('notifications', `/notifications/${notification.notification_id}`, {
    body: convertNotificationFormValues(body),
  })
}

export function deleteNotification(notification: NotificationItem) {
  return API.del('notifications', `/notifications/${notification.notification_id}`, {})
}

export function fetchSlackChannels(): Promise<SlackChannel[]> {
  return API.get('notifications', '/notifications/slack-channels', {})
}

export function fetchSlackMembers(): Promise<SlackMember[]> {
  return API.get('notifications', '/notifications/slack-members', {})
}

type FetchSlackOauthUrlParams = { redirect_page_url: string }

export function fetchSlackOauthUrl({ redirect_page_url }: FetchSlackOauthUrlParams): Promise<{ url: string }> {
  return API.get('notifications', '/notifications/slack-oauth-url', {
    queryStringParameters: {
      redirect_page_url,
    },
  })
}

// --- Trigger-based notifications (e.g. door-held-open) ---
// These are keyed by a triggering event + site (triggering_id, a location/zone id),
// with an optional set of doors (device_groups) that may fire them. They deliver by
// email to subscribed users rather than the destination/activity integration model.

export enum TriggeringEvent {
  DoorHeldOpen = 'authorization-events-door-held-open',
}

export type NotificationDeviceGroup = { device_group_id: string; name: string }
export type NotificationSubscriber = { user_id: string; username: string }

export type SubscribableUser = {
  user_id: string
  username: string
  given_name?: string
  family_name?: string
}

export type TriggerNotification = {
  notification_id: string
  name: string
  triggering_event: TriggeringEvent
  triggering_id: string
  // Zone name resolved by the list endpoint only.
  related_name?: string | null
  // Doors that may fire this notification. Empty ⇒ fires site-wide.
  device_groups: NotificationDeviceGroup[]
  // Present on the list endpoint only.
  subscribed_users?: { username: string }[]
}

export type TriggerNotificationFormValues = {
  name: string
  triggering_event: TriggeringEvent
  triggering_id: string
  // [] clears the door filter (fires site-wide); a populated array replaces it.
  device_group_ids: string[]
}

function isTriggerNotification(item: { triggering_event?: string }): boolean {
  return !!item.triggering_event
}

// The list endpoint returns every tenant notification; callers filter by triggering_id.
export async function getTriggerNotifications(): Promise<TriggerNotification[]> {
  const response: (TriggerNotification & { triggering_event?: string })[] = await API.get(
    'notifications',
    '/notifications',
    {}
  )
  return response.filter(isTriggerNotification)
}

export function fetchTriggerNotification({ id }: { id: string }): Promise<TriggerNotification> {
  return API.get('notifications', `/notifications/${id}`, {})
}

export function createTriggerNotification(body: TriggerNotificationFormValues): Promise<TriggerNotification> {
  return API.post('notifications', '/notifications', { body })
}

export function updateTriggerNotification(
  id: string,
  body: TriggerNotificationFormValues
): Promise<TriggerNotification> {
  return API.put('notifications', `/notifications/${id}`, { body })
}

export function deleteTriggerNotification(notification: Pick<TriggerNotification, 'notification_id'>): Promise<void> {
  return API.del('notifications', `/notifications/${notification.notification_id}`, {})
}

// --- Notification subscriptions (which users receive the notification) ---

export function getNotificationSubscribers({ id }: { id: string }): Promise<NotificationSubscriber[]> {
  return API.get('notifications', `/notifications/${id}/subscriptions`, {})
}

// Gated on the notifications permission rather than users, so roles that manage
// notifications but cannot list users (location admin, SOC operator) still get options.
export function getSubscribableUsers(): Promise<SubscribableUser[]> {
  return API.get('notifications', '/notifications/subscribable-users', {})
}

export function addNotificationSubscribers(id: string, user_ids: string[]): Promise<NotificationSubscriber[]> {
  return API.post('notifications', `/notifications/${id}/subscriptions`, { body: { user_ids } })
}

export function removeNotificationSubscriber(id: string, userId: string): Promise<NotificationSubscriber[]> {
  return API.del('notifications', `/notifications/${id}/subscriptions/${userId}`, {})
}
